/**
 * ClientManagementSidebar - Simplified sidebar for client creation only
 * TASK-209: Refactored to focus solely on client creation
 *
 * Features:
 * - Client selection/creation via ClientSelector
 * - Clean, focused UI for quick client onboarding
 *
 * Upload functionality has been moved to DocumentsTab and specialized upload components
 */

import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { ClientCreateForm } from './ClientCreateForm';
import type { Client } from '../../services/clientService';
import { OnboardingProvider } from '../../contexts/OnboardingContext';
import { toast } from 'sonner';

interface ClientManagementSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ClientManagementSidebar: React.FC<ClientManagementSidebarProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  // Reset state when sidebar closes
  useEffect(() => {
    if (!isOpen) {
      // Small delay to allow animation to complete - no state to reset currently
      // Future: may add state management here if needed
    }
  }, [isOpen]);

  /**
   * Handle client selection/creation from ClientSelector
   * When a new client is created, close the sidebar and notify parent
   */
  const handleClientSelected = (client: Client) => {
    // Show success message
    toast.success(`Client "${client.name}" created successfully`);

    // Close sidebar after brief delay for user feedback
    setTimeout(() => {
      onClose();
      onSuccess();
    }, 500);
  };

  if (!isOpen) return null;

  return (
    <OnboardingProvider>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className="fixed top-0 right-0 w-96 h-full bg-white shadow-xl z-50 transform transition-transform flex flex-col">
        {/* Header */}
        <div className="bg-kielis-navy text-white p-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Add Client</h3>
            <button
              onClick={onClose}
              className="text-white opacity-70 hover:opacity-100 transition-opacity"
              aria-label="Close sidebar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Helpful Information */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
            <p className="text-blue-900 font-medium mb-2">
              Quick Client Creation
            </p>
            <p className="text-blue-800">
              Creates a <strong>prospect</strong> record with the basic details below — KYC,
              risk profile, and documents follow on the client detail page, and conversion to
              a full client stays behind those gates. Tip: the dashboard assistant can run this
              end-to-end (“Onboard a new prospective client”).
            </p>
          </div>

          {/* Creation Form */}
          <ClientCreateForm onClientCreated={handleClientSelected} />
        </div>
      </div>
    </OnboardingProvider>
  );
};

export default ClientManagementSidebar;
